// services/webrtc.service.js
class WebRTCService {
    constructor() {
        this.peerConnection = null;
        this.localStream = null;
        this.remoteStreams = new Map();
        this.signalRService = null;
        this.connectionAttempts = 0;
        this.maxConnectionAttempts = 3;
        this.iceCandidates = [];
        this.isNegotiating = false;
        this.isMobile = this.detectMobile();
        this.targetConnectionId = null;
        this.pendingIceCandidates = [];
        this.maxNegotiationTime = 15000;
        // Event handlers
        this.onRemoteStream = null;
        this.onConnectionStateChange = null;
        this.onIceConnectionStateChange = null;
        this.onError = null;
        this.onDataChannel = null;

        // Track connection states
        this.hasRemoteDescription = false;
        this.hasLocalDescription = false;

        this.startNegotiationMonitoring = this.startNegotiationMonitoring.bind(this);
        this.stopNegotiationMonitoring = this.stopNegotiationMonitoring.bind(this);
        this.checkNegotiationProgress = this.checkNegotiationProgress.bind(this);
        this.dropCallDueToTimeout = this.dropCallDueToTimeout.bind(this);
    }

    detectMobile() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }

    async initialize() {
        try {
            const configuration = {
                iceServers: [
                    // 1. TURN over UDP (primary relay)
                    {
                        urls: "turn:172.188.32.222:3478?transport=udp",
                        username: "test",
                        credential: "test123"
                    },
                    // 2. TURN over TCP (fallback for UDP-blocked networks)
                    {
                        urls: "turn:172.188.32.222:3478?transport=tcp",
                        username: "test",
                        credential: "test123"
                    },
                    // 3. TURN over TLS (enterprise networks)
                    {
                        urls: "turns:172.188.32.222:5349",
                        username: "test",
                        credential: "test123"
                    },
                    // 4. STUN server
                    {
                        urls: "stun:172.188.32.222:3478"
                    }
                ],
                // Critical for success rate
                iceTransportPolicy: 'all',
                iceCandidatePoolSize: 10,
                bundlePolicy: 'max-bundle',
                rtcpMuxPolicy: 'require'
            };

            this.peerConnection = new RTCPeerConnection(configuration);
            this.setupConnectionListeners();
            //console.log('WebRTC initialized with optimized configuration');
            return true;
        } catch (error) {
            console.error('Failed to initialize WebRTC:', error);
            this.handleError('initialize', error);
            return false;
        }
    }

    startNegotiationMonitoring() {
        this.negotiationStartTime = Date.now();
        this.lastConnectionState = this.peerConnection.connectionState;

        console.log('⏱️ Starting negotiation monitoring...');

        // Check every 2 seconds for changes
        this.negotiationTimeout = setInterval(this.checkNegotiationProgress, 2000);

        // Set overall timeout
        setTimeout(() => {
            if (this.isNegotiating) {
                this.dropCallDueToTimeout();
            }
        }, this.maxNegotiationTime);
    }

    // 🆕 Stop monitoring (when negotiation completes)
    stopNegotiationMonitoring() {
        if (this.negotiationTimeout) {
            clearInterval(this.negotiationTimeout);
            this.negotiationTimeout = null;
            console.log('✅ Negotiation monitoring stopped');
        }
        this.negotiationStartTime = null;
        this.lastConnectionState = null;
    }

    // 🆕 Check if negotiation is making progress
    checkNegotiationProgress() {
        if (!this.isNegotiating || !this.negotiationStartTime) {
            this.stopNegotiationMonitoring();
            return;
        }

        const currentState = this.peerConnection.connectionState;
        const elapsedTime = Date.now() - this.negotiationStartTime;

        console.log(`🔍 Negotiation check: ${currentState}, elapsed: ${elapsedTime}ms`);

        // Check if state has changed
        if (currentState !== this.lastConnectionState) {
            console.log(`🔄 Connection state changed: ${this.lastConnectionState} → ${currentState}`);
            this.lastConnectionState = currentState;

            // If we reached a stable state, stop monitoring
            if (currentState === 'connected' || currentState === 'failed') {
                this.stopNegotiationMonitoring();
                return;
            }
        }

        // Check if we're stuck in connecting/new for too long
        if ((currentState === 'connecting' || currentState === 'new') && elapsedTime > 10000) {
            console.warn('⚠️ Stuck in connecting state for too long');
        }
    }

    // 🆕 Handle timeout - drop the call
    dropCallDueToTimeout() {
        console.error('⏰ Negotiation timeout - dropping call');

        // Stop monitoring
        this.stopNegotiationMonitoring();

        // Emit event to notify components
        this.emitNegotiationTimeout();

        // End the call
        this.endCall();

        // You can also trigger a UI event here
        if (typeof this.onNegotiationTimeout === 'function') {
            this.onNegotiationTimeout();
        }
    }

    // 🆕 Emit custom event for timeout
    emitNegotiationTimeout() {
        const event = new CustomEvent('webrtc:negotiation-timeout', {
            detail: {
                callId: this.targetConnectionId,
                reason: 'Negotiation took too long without progress'
            }
        });
        window.dispatchEvent(event);
    }

    // 🆕 Also monitor during answer creation
    async createAnswer(offer) {
        try {
            // Start monitoring when creating answer
            this.startNegotiationMonitoring();

            await this.peerConnection.setRemoteDescription(offer);
            const answer = await this.peerConnection.createAnswer();
            await this.peerConnection.setLocalDescription(answer);

            return answer;
        } catch (error) {
            console.error('Error creating answer:', error);
            this.stopNegotiationMonitoring();
            throw error;
        }
    }



    setTargetConnectionId(connectionId) {
        this.targetConnectionId = connectionId;
        console.log('Target connection ID set:', connectionId);
    }

    async createOffer(targetConnectionId = null) {
        try {
            if (this.isNegotiating) {
                console.log('Already negotiating, waiting...');
                return null;
            }

            this.isNegotiating = true;
            console.log('Creating WebRTC offer...');

            if (targetConnectionId) {
                this.targetConnectionId = targetConnectionId;
            }

            // Ensure we have a local stream before creating offer
            if (!this.localStream) {
                console.log('No local stream, starting call first...');
                await this.startCall();
            }

            const offerOptions = {
                offerToReceiveAudio: true,
                offerToReceiveVideo: false,
                voiceActivityDetection: true,
                iceRestart: false
            };

            const offer = await this.peerConnection.createOffer(offerOptions);
            console.log('Offer created:', offer.type);

            await this.peerConnection.setLocalDescription(offer);
            console.log('Local description set successfully');
            this.hasLocalDescription = true;

            // Process any pending ICE candidates
            this.processPendingIceCandidates();

            this.isNegotiating = false;
            return offer;

        } catch (error) {
            this.isNegotiating = false;
            console.error('Error creating offer:', error);
            this.handleError('createOffer', error);
            throw error;
        }
    }

    async sendOfferToTarget(offer, targetConnectionId = null) {
        if (!this.signalRService) {
            throw new Error('SignalR service not set');
        }

        const targetId = targetConnectionId || this.targetConnectionId;

        if (!targetId) {
            throw new Error('No target connection ID specified for sending offer');
        }

        console.log('Sending WebRTC offer to connection:', targetId);
        await this.signalRService.sendOffer(targetId, offer);
    }

    setupConnectionListeners() {
        if (!this.peerConnection) return;

        // 🆕 IMPROVED: Track event handling
        this.peerConnection.ontrack = (event) => {
            console.log('🔄 Remote track received:', event);

            if (event.streams && event.streams.length > 0) {
                const stream = event.streams[0];
                console.log('Remote stream received with tracks:', stream.getTracks().map(t => t.kind));

                // Listen for track ended events
                event.track.onended = () => {
                    console.log('Remote track ended:', event.track.id);
                };

                event.track.onmute = () => {
                    console.log('Remote track muted:', event.track.id);
                };

                event.track.onunmute = () => {
                    console.log('Remote track unmuted:', event.track.id);
                };

                if (this.onRemoteStream) {
                    this.onRemoteStream(stream);
                }

                const remoteStreamEvent = new CustomEvent('remoteStream', {
                    detail: { stream, connectionId: this.targetConnectionId || 'default' }
                });
                window.dispatchEvent(remoteStreamEvent);
            }
        };

        // 🆕 IMPROVED: ICE candidate handling
        this.peerConnection.onicecandidate = (event) => {
            console.log("ICE candidate event:", event.candidate ? 'candidate generated' : 'all candidates gathered');

            if (event.candidate && this.signalRService) {
                console.log('Sending ICE candidate to:', this.targetConnectionId);

                if (this.targetConnectionId) {
                    this.signalRService.sendIceCandidate(event.candidate, this.targetConnectionId);
                } else {
                    console.warn('No target connection ID set for ICE candidate');
                }
            } else if (!event.candidate) {
                console.log('✅ All ICE candidates gathered');
            }
        };

        // 🆕 IMPROVED: Connection state handling
        this.peerConnection.onconnectionstatechange = () => {
            const state = this.peerConnection.connectionState;
            console.log('🔗 WebRTC connection state changed:', state);

            if (this.onConnectionStateChange) {
                this.onConnectionStateChange(state);
            }

            switch (state) {
                case 'connected':
                    console.log('✅ WebRTC connection established!');
                    this.connectionAttempts = 0;
                    break;
                case 'disconnected':
                    console.log('⚠️ WebRTC connection disconnected');
                    break;
                case 'failed':
                    console.error('❌ WebRTC connection failed');
                    this.handleConnectionFailure();
                    break;
                case 'closed':
                    console.log('🔒 WebRTC connection closed');
                    break;
            }
        };

        // 🆕 IMPROVED: ICE connection state handling
        this.peerConnection.oniceconnectionstatechange = () => {
            const state = this.peerConnection.iceConnectionState;
            console.log('🧊 ICE connection state changed:', state);

            if (this.onIceConnectionStateChange) {
                this.onIceConnectionStateChange(state);
            }

            switch (state) {
                case 'connected':
                    console.log('✅ ICE connected successfully!');
                    this.connectionAttempts = 0;
                    break;
                case 'completed':
                    console.log('✅ ICE gathering completed!');
                    break;
                case 'failed':
                    console.error('❌ ICE connection failed');
                    this.handleConnectionFailure();
                    break;
                case 'disconnected':
                    console.warn('⚠️ ICE connection disconnected');
                    break;
            }
        };

        // 🆕 IMPROVED: Negotiation handling
        this.peerConnection.onnegotiationneeded = async () => {
            if (this.isNegotiating) {
                console.log('Already negotiating, skipping...');
                return;
            }

            this.isNegotiating = true;
            console.log('🔄 Negotiation needed...');
            this.startNegotiationMonitoring();
            try {
                // Small delay to ensure everything is ready
                await new Promise(resolve => setTimeout(resolve, 100));

                const offer = await this.createOffer();
                if (offer && this.targetConnectionId) {
                    await this.sendOfferToTarget(offer);
                }
            } catch (error) {
                console.error('Negotiation failed:', error);
                this.handleError('negotiation', error);
            } finally {
                this.isNegotiating = false;
            }
        };

        // 🆕 NEW: Data channel handling
        this.peerConnection.ondatachannel = (event) => {
            console.log('Data channel received:', event.channel.label);
            if (this.onDataChannel) {
                this.onDataChannel(event.channel);
            }
        };

        // 🆕 NEW: Signaling state change
        this.peerConnection.onsignalingstatechange = () => {
            console.log('📶 Signaling state:', this.peerConnection.signalingState);
        };
    }

    // 🆕 NEW: Process pending ICE candidates
    processPendingIceCandidates() {
        if (this.pendingIceCandidates.length > 0 && this.peerConnection.remoteDescription) {
            console.log(`Processing ${this.pendingIceCandidates.length} pending ICE candidates`);

            this.pendingIceCandidates.forEach(async (candidate) => {
                try {
                    await this.peerConnection.addIceCandidate(candidate);
                    console.log('Pending ICE candidate added');
                } catch (error) {
                    console.error('Error adding pending ICE candidate:', error);
                }
            });

            this.pendingIceCandidates = [];
        }
    }

    handleConnectionFailure() {
        this.connectionAttempts++;

        if (this.connectionAttempts <= this.maxConnectionAttempts) {
            console.log(`Connection attempt ${this.connectionAttempts}/${this.maxConnectionAttempts}, restarting...`);
            setTimeout(() => this.restartConnection(), 2000 * this.connectionAttempts);
        } else {
            console.error('Max connection attempts reached');
            this.handleError('connection', new Error('Failed to establish connection after multiple attempts'));
        }
    }

    async restartConnection() {
        try {
            console.log('🔄 Restarting WebRTC connection...');

            const hadLocalStream = !!this.localStream;
            const savedLocalStream = this.localStream;

            // Close existing connection
            if (this.peerConnection) {
                this.peerConnection.close();
            }

            // Reinitialize
            await this.initialize();

            // Re-add local stream if it existed
            if (hadLocalStream && savedLocalStream) {
                const audioTracks = savedLocalStream.getAudioTracks();
                audioTracks.forEach(track => {
                    this.peerConnection.addTrack(track, savedLocalStream);
                });
                this.localStream = savedLocalStream;
            }

            console.log('✅ WebRTC connection restarted');

        } catch (error) {
            console.error('Error restarting connection:', error);
            this.handleError('restart', error);
        }
    }

    handleError(context, error) {
        console.error(`WebRTC error in ${context}:`, error);

        if (this.onError) {
            this.onError(context, error);
        }

        const errorEvent = new CustomEvent('webrtcError', {
            detail: { context, error: error.message }
        });
        window.dispatchEvent(errorEvent);
    }

    async startCall() {
        try {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                throw new Error('Media devices not supported in this browser');
            }

            // Simplified constraints for better compatibility
            const constraints = {
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    channelCount: 1,
                    sampleRate: 8000,
                    sampleSize: 16,
                    latency: 0.01
                },
                video: false
            };

            console.log('🎤 Requesting microphone access...');

            let stream;
            try {
                stream = await navigator.mediaDevices.getUserMedia(constraints);
            } catch (error) {
                console.log('First attempt failed, trying simpler constraints...');
                // Fallback to basic constraints
                const simpleConstraints = {
                    audio: true,
                    video: false
                };
                stream = await navigator.mediaDevices.getUserMedia(simpleConstraints);
            }

            this.localStream = stream;

            const audioTracks = this.localStream.getAudioTracks();
            if (audioTracks.length === 0) {
                throw new Error('No audio tracks available');
            }

            console.log('✅ Audio tracks obtained:', audioTracks.length);

            // Add tracks to peer connection
            audioTracks.forEach(track => {
                this.peerConnection.addTrack(track, this.localStream);
                console.log('Added audio track:', track.id, 'enabled:', track.enabled, 'muted:', track.muted);
            });

            console.log('✅ Microphone access granted and ready for WebRTC');
            return true;

        } catch (error) {
            console.error('❌ Error starting call:', error);

            if (error.name === 'NotAllowedError') {
                throw new Error('Microphone permission denied. Please allow microphone access and try again.');
            } else if (error.name === 'NotFoundError') {
                throw new Error('No microphone found. Please check your device has a microphone.');
            } else if (error.name === 'NotReadableError') {
                throw new Error('Microphone is being used by another app. Please close other apps using the microphone.');
            }

            this.handleError('startCall', error);
            throw error;
        }
    }

    checkMobileCompatibility() {
        const issues = [];

        if (!navigator.mediaDevices) {
            issues.push('Media devices not supported');
        }

        if (!navigator.mediaDevices.getUserMedia) {
            issues.push('getUserMedia not supported');
        }

        if (!window.RTCPeerConnection) {
            issues.push('WebRTC not supported');
        }

        if (this.isMobile) {
            const userAgent = navigator.userAgent;
            if (/iPhone|iPad|iPod/.test(userAgent)) {
                issues.push('iOS may require user interaction for microphone access');
            }
            if (/Android/.test(userAgent)) {
                issues.push('Android may require HTTPS for microphone access');
            }
        }

        return {
            isMobile: this.isMobile,
            isCompatible: issues.length === 0,
            issues: issues,
            userAgent: navigator.userAgent
        };
    }

    async handleOffer(offer, connectionId) {
        try {
            console.log('📥 Handling WebRTC offer from:', connectionId);

            // Ensure we have local media
            if (!this.localStream) {
                await this.startCall();
            }

            await this.peerConnection.setRemoteDescription(offer);
            this.hasRemoteDescription = true;
            console.log('Remote description set');

            const answer = await this.peerConnection.createAnswer();
            await this.peerConnection.setLocalDescription(answer);
            this.hasLocalDescription = true;

            console.log('✅ WebRTC answer created for:', connectionId);

            // Process any pending ICE candidates
            this.processPendingIceCandidates();

            return answer;
        } catch (error) {
            console.error('Error handling offer:', error);
            this.handleError('handleOffer', error);
            throw error;
        }
    }

    async handleAnswer(answer) {
        try {
            console.log('📥 Handling WebRTC answer...');
            await this.peerConnection.setRemoteDescription(answer);
            this.hasRemoteDescription = true;

            console.log('✅ WebRTC answer processed - connection should be established');

            // Process any pending ICE candidates
            this.processPendingIceCandidates();

        } catch (error) {
            console.error('Error handling answer:', error);
            this.handleError('handleAnswer', error);
            throw error;
        }
    }

    async addIceCandidate(candidate) {
        try {
            if (this.peerConnection.remoteDescription && this.peerConnection.localDescription) {
                await this.peerConnection.addIceCandidate(candidate);
                console.log('✅ ICE candidate added successfully');
            } else {
                // Store candidate for later when remote description is set
                console.log('⏳ Storing ICE candidate for later (waiting for descriptions)');
                this.pendingIceCandidates.push(candidate);
            }
        } catch (error) {
            console.error('Error adding ICE candidate:', error);
            // Don't throw - ICE candidates can fail without breaking the call
        }
    }

    // 🆕 NEW: Get connection status
    getConnectionStatus() {
        if (!this.peerConnection) {
            return 'not_initialized';
        }

        return {
            signalingState: this.peerConnection.signalingState,
            iceConnectionState: this.peerConnection.iceConnectionState,
            connectionState: this.peerConnection.connectionState,
            hasLocalStream: !!this.localStream,
            hasRemoteDescription: this.hasRemoteDescription,
            hasLocalDescription: this.hasLocalDescription,
            isNegotiating: this.isNegotiating
        };
    }

    // 🆕 NEW: Check if audio is working
    checkAudioStatus() {
        const status = {
            local: {
                hasStream: !!this.localStream,
                tracks: this.localStream ? this.localStream.getAudioTracks().map(t => ({
                    id: t.id,
                    enabled: t.enabled,
                    muted: t.muted,
                    readyState: t.readyState
                })) : []
            },
            remote: {
                hasStreams: this.remoteStreams.size > 0,
                streams: Array.from(this.remoteStreams.entries()).map(([id, stream]) => ({
                    id,
                    tracks: stream.getAudioTracks().map(t => ({
                        id: t.id,
                        enabled: t.enabled,
                        muted: t.muted,
                        readyState: t.readyState
                    }))
                }))
            }
        };

        console.log('Audio status:', status);
        return status;
    }

    async endCall() {
        console.log('🔚 Ending WebRTC call...');
        this.stopNegotiationMonitoring();

        this.isNegotiating = false;
        this.connectionAttempts = 0;
        this.iceCandidates = [];
        this.pendingIceCandidates = [];
        this.hasRemoteDescription = false;
        this.hasLocalDescription = false;

        if (this.localStream) {
            this.localStream.getTracks().forEach(track => {
                track.stop();
                console.log('Stopped track:', track.id);
            });
            this.localStream = null;
        }

        if (this.peerConnection) {
            this.peerConnection.close();
            this.peerConnection = null;
        }

        this.remoteStreams.clear();
        console.log('✅ WebRTC call ended completely');
    }

    setSignalRService(signalRService) {
        this.signalRService = signalRService;
    }
}

// Freeze the instance to prevent modification
export default new WebRTCService();